import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield, Save, Loader2, RotateCcw, Plus, Trash2, X, Search, Eye,
  Edit3, CheckCircle2, AlertCircle, RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const PERM = process.env.REACT_APP_BACKEND_URL + '/api/permissions';

const ROLE_LABELS = {
  tenant_admin: 'Tenant Admin',
  hr_admin: 'HR Admin',
  payroll_admin: 'Payroll Admin',
  attendance_admin: 'Attendance Admin',
  finance_reviewer: 'Finance Reviewer',
  department_manager: 'Dept Manager',
  employee: 'Employee',
};

const ACTION_LABELS = { view: 'View', edit: 'Edit', delete: 'Delete', approve: 'Approve' };
const ACTION_COLORS = {
  view: 'text-blue-600',
  edit: 'text-emerald-600',
  delete: 'text-red-600',
  approve: 'text-purple-600',
};

const Roles = () => {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState({ modules: [], actions: ['view', 'edit', 'delete', 'approve'] });
  const [matrix, setMatrix] = useState([]);                  // raw API response
  const [activeRole, setActiveRole] = useState('tenant_admin');
  const [draft, setDraft] = useState(new Set());             // in-memory edits
  const [originalDraft, setOriginalDraft] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState({ name: '', description: '' });
  const isTenantAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        axios.get(`${PERM}/catalog`, { withCredentials: true }),
        axios.get(`${PERM}/matrix`, { withCredentials: true }),
      ]);
      setCatalog(c.data);
      setMatrix(m.data.roles);
      // Default to first role
      const first = m.data.roles[0]?.role || 'tenant_admin';
      setActiveRole(first);
      const perms = new Set(m.data.roles.find((r) => r.role === first)?.permissions || []);
      setDraft(perms);
      setOriginalDraft(new Set(perms));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load permission matrix');
    } finally {
      setLoading(false);
    }
  };

  const switchRole = (role) => {
    if (draft.size !== originalDraft.size || [...draft].some((p) => !originalDraft.has(p))) {
      if (!window.confirm('You have unsaved changes. Switch role and lose them?')) return;
    }
    setActiveRole(role);
    const perms = new Set(matrix.find((r) => r.role === role)?.permissions || []);
    setDraft(perms);
    setOriginalDraft(new Set(perms));
  };

  const togglePerm = (key) => {
    const next = new Set(draft);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setDraft(next);
  };

  const toggleTab = (modKey, tabKey, tabActions, allOn) => {
    const next = new Set(draft);
    tabActions.forEach((a) => {
      const k = `${modKey}.${tabKey}.${a}`;
      if (allOn) next.delete(k);
      else next.add(k);
    });
    setDraft(next);
  };

  const toggleModule = (mod, allOn) => {
    const next = new Set(draft);
    mod.tabs.forEach((t) => {
      t.actions.forEach((a) => {
        const k = `${mod.key}.${t.key}.${a}`;
        if (allOn) next.delete(k);
        else next.add(k);
      });
    });
    setDraft(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${PERM}/matrix`, {
        role: activeRole,
        permissions: [...draft],
      }, { withCredentials: true });
      toast.success('Permissions saved');
      setOriginalDraft(new Set(draft));
      // Refresh matrix data + the current user's own permissions (in case they edited their own role).
      await fetchAll();
      if (refresh) refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (activeRole.startsWith('custom:')) {
      toast.error('Custom roles cannot be reset — edit or delete them instead.');
      return;
    }
    if (!window.confirm(`Reset "${ROLE_LABELS[activeRole] || activeRole}" back to default permissions?`)) return;
    setSaving(true);
    try {
      await axios.post(`${PERM}/matrix/${activeRole}/reset`, null, { withCredentials: true });
      toast.success('Reset to defaults');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleForm.name.trim()) return;
    try {
      const { data } = await axios.post(`${PERM}/custom-roles`, newRoleForm, { withCredentials: true });
      toast.success('Role created');
      setShowCreateRole(false);
      setNewRoleForm({ name: '', description: '' });
      await fetchAll();
      switchRole(data.key);
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const handleDeleteRole = async () => {
    if (activeRole === 'tenant_admin') {
      toast.error('The Tenant Admin role cannot be deleted.');
      return;
    }
    const isCustomRole = activeRole.startsWith('custom:');
    const roleLabel = isCustomRole
      ? (activeRoleMeta?.name || 'this custom role')
      : (ROLE_LABELS[activeRole] || activeRole);
    if (!window.confirm(
      isCustomRole
        ? `Delete "${roleLabel}"? Users assigned to it will lose access.`
        : `Delete the predefined role "${roleLabel}" from this tenant?\n\n` +
          'Users still on this role must be reassigned first. You can restore it later.',
    )) return;
    try {
      if (isCustomRole) {
        const id = activeRole.split(':')[1];
        await axios.delete(`${PERM}/custom-roles/${id}`, { withCredentials: true });
      } else {
        await axios.delete(`${PERM}/builtin-roles/${activeRole}`, { withCredentials: true });
      }
      toast.success('Role deleted');
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const isDirty = useMemo(() => {
    if (draft.size !== originalDraft.size) return true;
    for (const p of draft) if (!originalDraft.has(p)) return true;
    return false;
  }, [draft, originalDraft]);

  const filteredCatalog = useMemo(() => {
    if (!search.trim()) return catalog.modules;
    const q = search.toLowerCase();
    return catalog.modules
      .map((m) => ({
        ...m,
        tabs: m.tabs.filter(
          (t) => t.label.toLowerCase().includes(q) || m.label.toLowerCase().includes(q),
        ),
      }))
      .filter((m) => m.tabs.length > 0);
  }, [catalog, search]);

  const activeRoleMeta = matrix.find((r) => r.role === activeRole);
  const isCustom = activeRole.startsWith('custom:');

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  if (!isTenantAdmin) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto py-20 text-center" data-testid="roles-readonly">
          <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Permission Matrix</h2>
          <p className="text-sm text-slate-600 mt-2">
            Only the Tenant Admin can view or edit role permissions. Contact your tenant administrator if you need a change.
          </p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="roles-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Shield className="inline-block w-7 h-7 mr-2 mb-1" /> Roles & Permissions
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Tick or untick exactly which modules, tabs, and actions each role can access.
              Sidebar visibility and direct-URL access both honour this matrix.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={async () => {
                try {
                  const { data } = await axios.post(`${PERM}/force-sync`, {}, { withCredentials: true });
                  const total = data.overrides_updated + data.custom_roles_updated;
                  if (total === 0) {
                    toast.success('All roles already have the latest permissions — nothing to sync.');
                  } else {
                    toast.success(`Synced ${total} role(s). Log out & log in to see new menu items.`);
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Force-sync failed');
                }
              }}
              variant="outline"
              data-testid="force-sync-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Re-apply newly-shipped permission keys to all roles in this tenant. Use this if a sidebar item is missing after a deploy."
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Force Sync Permissions
            </Button>
            <Button onClick={() => setShowCreateRole(true)} className="bg-[#0052FF] text-white hover:bg-[#0040CC]" data-testid="create-role-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Custom Role
            </Button>
          </div>
        </div>

        {/* Role tabs */}
        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
          {matrix.map((r) => {
            const label = r.is_custom ? r.name : (ROLE_LABELS[r.role] || r.role);
            const active = r.role === activeRole;
            return (
              <button
                key={r.role}
                onClick={() => switchRole(r.role)}
                data-testid={`role-tab-${r.role.replace(':', '-')}`}
                className={`group relative px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? 'bg-[#0052FF] text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                {label}
                <span className={`ml-2 text-xs ${active ? 'text-blue-200' : 'text-slate-400'}`}>
                  {r.permissions.length}
                </span>
                {r.is_overridden && !active && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full" title="Overridden from defaults" />
                )}
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search module or tab..."
              data-testid="perm-search"
              className="w-full pl-9 pr-3 h-10 border border-slate-300 rounded-md text-sm bg-white"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-slate-500">
              <strong className="text-slate-800">{draft.size}</strong> of {catalog.modules.reduce((n, m) => n + m.tabs.reduce((nn, t) => nn + t.actions.length, 0), 0)} permissions selected
            </span>
            {!isCustom && activeRoleMeta?.is_overridden && (
              <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset to Defaults
              </Button>
            )}
            {/* Stage 73.20ec — Delete allowed for every role except tenant_admin. */}
            {activeRole !== 'tenant_admin' && (
              <Button variant="outline" onClick={handleDeleteRole} disabled={saving} data-testid="delete-role-btn" className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-1" /> Delete Role
              </Button>
            )}
            <Button onClick={handleSave} disabled={!isDirty || saving} className="bg-[#0052FF] text-white hover:bg-[#0040CC]" data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </div>

        {isDirty && (
          <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-900 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Unsaved changes. Click <strong>Save Changes</strong> to apply.
          </div>
        )}

        {/* Matrix table */}
        <div className="space-y-3">
          {filteredCatalog.map((mod) => {
            const totalActions = mod.tabs.reduce((n, t) => n + t.actions.length, 0);
            const checkedCount = mod.tabs.reduce(
              (n, t) => n + t.actions.filter((a) => draft.has(`${mod.key}.${t.key}.${a}`)).length,
              0,
            );
            const allOn = checkedCount === totalActions;
            const someOn = checkedCount > 0 && checkedCount < totalActions;
            return (
              <details key={mod.key} className="bg-white border border-slate-200 rounded-xl" open={!!search.trim() || mod.tabs.length <= 4}>
                <summary className="cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={(el) => el && (el.indeterminate = someOn)}
                      onChange={() => toggleModule(mod, allOn)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`mod-toggle-${mod.key}`}
                      className="w-4 h-4 cursor-pointer"
                    />
                    <span className="text-sm font-semibold text-slate-900">{mod.label}</span>
                    <span className="text-xs text-slate-500">
                      {checkedCount}/{totalActions}
                    </span>
                  </div>
                </summary>
                <div className="border-t border-slate-100">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-2 w-1/2">Tab</th>
                        {catalog.actions.map((a) => (
                          <th key={a} className={`text-center text-[11px] font-semibold uppercase tracking-wider px-2 py-2 ${ACTION_COLORS[a]}`}>
                            {ACTION_LABELS[a] || a}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mod.tabs.map((tab) => {
                        const tabAllOn = tab.actions.every((a) => draft.has(`${mod.key}.${tab.key}.${a}`));
                        return (
                          <tr key={tab.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                            <td className="px-4 py-2 text-sm text-slate-800">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={tabAllOn}
                                  onChange={() => toggleTab(mod.key, tab.key, tab.actions, tabAllOn)}
                                  data-testid={`tab-toggle-${mod.key}-${tab.key}`}
                                  className="w-4 h-4"
                                />
                                <span>{tab.label}</span>
                                <span className="text-[10px] text-slate-400 font-mono ml-1">{tab.path}</span>
                              </label>
                            </td>
                            {catalog.actions.map((a) => {
                              const supported = tab.actions.includes(a);
                              const k = `${mod.key}.${tab.key}.${a}`;
                              const checked = draft.has(k);
                              return (
                                <td key={a} className="text-center px-2 py-2">
                                  {supported ? (
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => togglePerm(k)}
                                      data-testid={`perm-${k}`}
                                      className="w-4 h-4 cursor-pointer"
                                    />
                                  ) : (
                                    <span className="text-slate-300 text-sm">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            );
          })}
        </div>

        {/* Create custom role modal */}
        {showCreateRole && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreateRole(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="create-role-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Create Custom Role</h3>
                <button onClick={() => setShowCreateRole(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <form onSubmit={handleCreateRole} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role Name *</label>
                  <input
                    value={newRoleForm.name}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, name: e.target.value })}
                    data-testid="new-role-name"
                    placeholder="e.g. Regional HR Lead"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={newRoleForm.description}
                    onChange={(e) => setNewRoleForm({ ...newRoleForm, description: e.target.value })}
                    rows={2}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  The role starts with no permissions. Tick what you want on the matrix once it's created.
                </p>
                <Button type="submit" data-testid="submit-role-btn" className="w-full bg-[#0052FF] text-white hover:bg-[#0040CC]">
                  Create Role
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Roles;
